import _keyBy from 'lodash.keyby';
import _mapValues from 'lodash.mapvalues';
import { isRasterLayer, isGeoJsonLayer } from '../../guards';
import { DEFAULT_MIN_ZOOM_TO_VIEW_LABELS, ICON_CONTENT_MAP } from '../../constants';
import { isPoint, isLineString, isPolygon } from '../geom';
import { getLayerSymbols, getMapboxLayerStyleOptions, isCategorizedByStatus } from '../style';
import {
  vetroLayerToVectorSourceName,
  vetroLayerToSourceLayerId,
  vetroLayerToRasterSourceName,
  vetroLayerToGeoJsonSourceName,
} from './conversions';
import { getMapboxLabelStyle, getMapboxLabelTextFieldProperty } from './labelFeatures';
import { addHighlightSource, addHighlightLayer } from './highlightFeatures';
import {
  getHighlightMapboxLayerId,
  getLabelMapboxLayerId,
  getPolygonOutlineMapboxLayerId,
  getRasterMapboxLayerId,
} from './names';
import { isPolygonOutlineMapboxLayer, isCircleLayer } from './types';

export const shouldDrawLayer = (layer) => {
  if (!layer) return false;
  if (layer.externalUrl) return true;
  if (!layer.featureTable) {
    // TODO remove this check when layers have been populated in db
    // Customers, Busines Locations, and other nnps do not have rows in any table
    // so the feature_type update missed them
    console.warn('no source name'); // eslint-disable-line no-console
    return false;
  }
  if (isPoint(layer.geomType)) {
    const { iconClass } = getLayerSymbols(layer)[0];
    return Boolean(ICON_CONTENT_MAP[iconClass]);
  }
  return true;
};

// Vector and geojson layers are the same, except that GeoJson layers do not have a sourceLayer.
export const getVectorOrGeoJsonLayerConfig = ({
  layer,
  mapboxLayerId,
  useImperialUnits,
  visibility,
  labelMinZoom = DEFAULT_MIN_ZOOM_TO_VIEW_LABELS,
}) => {
  const { geomType } = layer;
  // we may be adding the accompanying vector layer for a raster layer, so check for geojson, not vector
  const isGeoJson = isGeoJsonLayer(layer);
  const sourceName = isGeoJson
    ? vetroLayerToGeoJsonSourceName(layer)
    : vetroLayerToVectorSourceName(layer);
  const sourceLayer = isGeoJson ? null : vetroLayerToSourceLayerId(layer);

  const style = getMapboxLayerStyleOptions({ layer, visibility });

  if (!isGeoJson) {
    style.sourceLayer = sourceLayer;
  }

  // Get corresponding mapbox-gl types from Vetro's geomTypes
  let type;
  if (isLineString(geomType) || isPolygonOutlineMapboxLayer(layer)) {
    type = 'line';
  } else if (isPolygon(geomType)) {
    type = 'fill';
  } else if (isCircleLayer(layer)) {
    type = 'circle';
  } else if (isPoint(geomType)) {
    type = 'symbol';
  } else {
    throw new Error(`Unrecognized geomType: ${geomType}`);
  }

  const labelMapboxLayerId = getLabelMapboxLayerId(mapboxLayerId);
  const labelStyle = getMapboxLabelStyle({
    layer,
    useImperialUnits,
    visibility,
    minZoomLevel: labelMinZoom,
  });

  return { sourceName, labelStyle, labelMapboxLayerId, type, style };
};

const addVectorOrGeoJsonLayer = ({ map, layer, mapboxLayerId, useImperialUnits, visibility }) => {
  const { sourceName, labelStyle, labelMapboxLayerId, type, style } = getVectorOrGeoJsonLayerConfig(
    {
      layer,
      mapboxLayerId,
      useImperialUnits,
      visibility,
    },
  );

  if (type === 'line') {
    map.U.addLine(mapboxLayerId, sourceName, style);
  } else if (type === 'symbol') {
    map.U.addSymbol(mapboxLayerId, sourceName, style);
  } else if (type === 'fill') {
    map.U.addFill(mapboxLayerId, sourceName, style);
  } else {
    throw new Error(`Unrecognized mapbox-gl type: ${type}`);
  }

  if (labelStyle) {
    map.U.addSymbol(labelMapboxLayerId, sourceName, labelStyle);
  } else {
    map.U.removeLayer(labelMapboxLayerId);
  }
};

const addGeoJsonLayer = addVectorOrGeoJsonLayer;

const addVectorLayer = addVectorOrGeoJsonLayer;

const addRasterLayer = ({ map, layer, mapboxLayerId, visibility }) => {
  const { rasterId } = layer;

  const sourceName = vetroLayerToRasterSourceName(layer);

  const rasterMapboxId = getRasterMapboxLayerId(mapboxLayerId);

  map.U.addRasterLayer(rasterMapboxId, sourceName, {
    id: rasterId,
    type: 'raster',
    visibility: visibility === 'visible' ? 'visible' : 'none',
    minzoom: 4,
  });

  // mapbox cannot detect a hover state on raster layers,
  // so we add a ~~hidden~~ vector (potentially 'visible', but always with opacity of 0) layer that mapbox can query for features,
  // and therefore set correct hover cursor :D
  // opacity will be set to 0 by virute of the truthyness of layer.rasterId
  // https://github.com/mapbox/mapbox-gl-js/issues/9112
  //
  // we only add a vector layer for _internal_ raster layers
  if (!layer.externalUrl) {
    addVectorLayer({ map, layer, mapboxLayerId, visibility });
  }
};

const refreshLineLabel = ({ layer, map, mapboxLayerId, useImperialUnits }) => {
  const labelMapboxLayerId = getLabelMapboxLayerId(mapboxLayerId);
  const newProperty = getMapboxLabelTextFieldProperty(layer, useImperialUnits);
  if (newProperty.length > 1) {
    map.U.setProperty(labelMapboxLayerId, 'text-field', newProperty);
  }
};

/**
 * Draw a single mapbox layer if it does not exist on the map. If it does,
 * set its visibility.
 * @param {*} d destructured args
 * @param {object} d.map napbox map instance
 * @param {object} d.layer VETRO layer object
 * @param {string} d.mapboxLayerId mapbox layer id for this layer
 * @param {boolean} d.visibility whether the layer is visibile
 */
export const addMapboxLayer = ({
  map,
  layer,
  mapboxLayerId,
  refreshUnitLabels,
  useImperialUnits,
  visibility,
}) => {
  // We use a different layer ID prefix for Rasters, so check the right one here
  const renderedLayerId = isRasterLayer(layer)
    ? getRasterMapboxLayerId(mapboxLayerId)
    : mapboxLayerId;

  if (map.getLayer(renderedLayerId)) {
    if (refreshUnitLabels && isLineString(layer.geomType)) {
      refreshLineLabel({ layer, map, mapboxLayerId, useImperialUnits });
    }

    map.U.toggle(renderedLayerId, visibility === 'visible');
  } else if (isRasterLayer(layer)) {
    addRasterLayer({ map, layer, mapboxLayerId, visibility });
  } else if (isGeoJsonLayer(layer)) {
    addGeoJsonLayer({ map, layer, mapboxLayerId, useImperialUnits, visibility });
  } else {
    addVectorLayer({ map, layer, mapboxLayerId, useImperialUnits, visibility });

    if (isPolygon(layer)) {
      const outlineLayer = {
        ...layer,
        geomType: 'LineString',
      };

      addVectorLayer({
        map,
        layer: outlineLayer,
        mapboxLayerId: getPolygonOutlineMapboxLayerId(mapboxLayerId),
        useImperialUnits,
        visibility,
      });
    }
  }
};

export const addMapboxLayers = ({
  map,
  mapboxToVetroLayerIdMap,
  layers,
  activeLayerIds,
  useImperialUnits,
  refreshUnitLabels,
}) => {
  addHighlightSource(map);
  const layersKeyedById = _keyBy(layers, 'id');
  Object.entries(mapboxToVetroLayerIdMap)
    .map(([mapboxLayerId, layerId]) => {
      const layer = layersKeyedById[layerId];
      return { mapboxLayerId, layer };
    })
    .filter(({ layer }) => shouldDrawLayer(layer))
    .forEach(({ mapboxLayerId, layer }) => {
      const visibility = activeLayerIds.has(layer.id) ? 'visible' : 'none';
      addMapboxLayer({
        map,
        layer,
        mapboxLayerId,
        refreshUnitLabels,
        useImperialUnits,
        visibility,
      });
      addHighlightLayer({ map, layer });
    });
};

export const removeMapboxLayer = (map, mapboxLayerId) => {
  const labelLayerId = getLabelMapboxLayerId(mapboxLayerId);
  const highlightMapboxLayer = getHighlightMapboxLayerId(mapboxLayerId);
  const highlightFillMapboxLayer = getHighlightMapboxLayerId(mapboxLayerId, true);
  const polygonOutlineLayer = getPolygonOutlineMapboxLayerId(mapboxLayerId);
  map.U.removeLayer(mapboxLayerId);
  map.U.removeLayer(labelLayerId);
  map.U.removeLayer(highlightMapboxLayer);
  map.U.removeLayer(polygonOutlineLayer);
  map.U.removeLayer(highlightFillMapboxLayer);
};

const getCoreOrderExpression = (
  values,
  op,
  categorizedAttributeLabel,
  reverse = false,
  displayOrderByValue = {},
) => {
  const l = values.length + 1;
  const middleConditions = values.flatMap((val, i) => {
    const orderNum = displayOrderByValue[val] ?? i;

    return [
      [op, ['get', categorizedAttributeLabel], val],
      reverse ? orderNum / l : (l - orderNum) / l,
    ];
  });

  return ['case', ...middleConditions, reverse ? (l - 1) / l : 0];
};

const getDomainValues = (layer, categorizedAttributeLabel, featureStatuses = []) =>
  featureStatuses.length > 0
    ? featureStatuses.map(({ id }) => id)
    : layer.availableAttributes[categorizedAttributeLabel].permittedValues;

const getThresholdValues = (layer) =>
  Object.keys(layer.style.symbols)
    .filter((val) => val !== 'Other')
    .map(Number)
    .sort((a, b) => a - b);

export const getCategorizedOrderExpression = (
  layer,
  { reverse = false, featureStatuses = [] } = {},
) => {
  const categorizedAttributeLabel = layer?.style?.categorizedAttributeLabel;

  if (!categorizedAttributeLabel) return 0;

  const categorizedByStatus = isCategorizedByStatus(categorizedAttributeLabel);

  const hasDomainValues =
    (categorizedByStatus && featureStatuses.length > 0) ||
    !!layer.availableAttributes[categorizedAttributeLabel].permittedValues;

  const values = hasDomainValues
    ? getDomainValues(layer, categorizedAttributeLabel, categorizedByStatus ? featureStatuses : [])
    : getThresholdValues(layer);

  const op = hasDomainValues ? '==' : '<';

  const featureStatusDisplayOrderById = categorizedByStatus
    ? _mapValues(_keyBy(featureStatuses, 'id'), (fs) => fs.displayOrder)
    : {};

  return getCoreOrderExpression(
    values,
    op,
    categorizedAttributeLabel,
    reverse,
    featureStatusDisplayOrderById,
  );
};
